import type { ChartSeries } from "../../information-retrieval/components/InformationRetrievalChart";
import {
	GROUP_COLORS,
	type SummaryKey,
} from "../../information-retrieval/models/InformationRetrieval";
import type { SummaryValues } from "../../information-retrieval/utils/aggregate";
import type {
	StraightenerGroupRow,
	StraightenerInformationRetrievalRow,
} from "../models/StraightenerInformationRetrieval";

export const rollLabel = (rollNumber: number): string => `Roll ${rollNumber}`;

/** Columns come from the data: one per roll the runs recorded, in roll order. */
export const buildRolls = (
	rows: StraightenerInformationRetrievalRow[],
): string[] =>
	[...new Set(rows.map((r) => r.RollNumber))]
		.sort((a, b) => a - b)
		.map(rollLabel);

const groupLabel = (date: string | null, shift: string | null): string => {
	const cleanDate = date ? date.slice(0, 10) : "";
	if (cleanDate && shift) return `${cleanDate} - ${shift}`;
	return cleanDate || shift || "All";
};

export const buildGroups = (
	rows: StraightenerInformationRetrievalRow[],
): StraightenerGroupRow[] => {
	const groups = new Map<string, StraightenerGroupRow>();

	for (const row of rows) {
		const key = `${row.Date ?? ""}|${row.Shift ?? ""}`;
		let group = groups.get(key);

		if (!group) {
			group = {
				key,
				label: groupLabel(row.Date, row.Shift),
				legendColor: GROUP_COLORS[groups.size % GROUP_COLORS.length],
				recipeName: row.RecipeName ?? "",
				stands: {},
				rows: [],
			};
			groups.set(key, group);
		}

		group.rows.push(row);
		group.stands[rollLabel(row.RollNumber)] = {
			avg: row.ActualAvg,
			target: row.TargetAvg,
			min: row.RecipeMinAvg,
			max: row.RecipeMaxAvg,
			active: row.ActualCount > 0,
		};
	}

	return [...groups.values()];
};

interface RollTotals {
	actualSum: number;
	actualCount: number;
	sumSquares: number;
	min: number | null;
	max: number | null;
	targetSum: number;
	targetCount: number;
	recipeMinSum: number;
	recipeMinCount: number;
	recipeMaxSum: number;
	recipeMaxCount: number;
	bandCount: number;
	outOfLimits: number;
}

/**
 * Adds the selected groups up per roll. Every average is a total over a count
 * of the runs that carry that value, so groups weigh by how many runs they hold
 * and a run without a recipe never counts as a zero.
 */
const totalsByRoll = (
	groups: StraightenerGroupRow[],
	selectedKeys: string[],
	rolls: string[],
): Map<string, RollTotals> => {
	const totals = new Map<string, RollTotals>();
	const selected = groups.filter((g) => selectedKeys.includes(g.key));

	for (const roll of rolls) {
		for (const group of selected) {
			const row = group.rows.find((r) => rollLabel(r.RollNumber) === roll);
			if (!row || row.ActualCount === 0) continue;

			const t = totals.get(roll) ?? {
				actualSum: 0,
				actualCount: 0,
				sumSquares: 0,
				min: null,
				max: null,
				targetSum: 0,
				targetCount: 0,
				recipeMinSum: 0,
				recipeMinCount: 0,
				recipeMaxSum: 0,
				recipeMaxCount: 0,
				bandCount: 0,
				outOfLimits: 0,
			};

			t.actualSum += row.ActualSum ?? 0;
			t.actualCount += row.ActualCount;
			t.sumSquares += row.ActualSumSquares ?? 0;
			if (row.ActualMin !== null)
				t.min = t.min === null ? row.ActualMin : Math.min(t.min, row.ActualMin);
			if (row.ActualMax !== null)
				t.max = t.max === null ? row.ActualMax : Math.max(t.max, row.ActualMax);
			t.targetSum += row.TargetSum ?? 0;
			t.targetCount += row.RecipeCount;
			t.recipeMinSum += row.RecipeMinSum ?? 0;
			t.recipeMinCount += row.RecipeMinCount;
			t.recipeMaxSum += row.RecipeMaxSum ?? 0;
			t.recipeMaxCount += row.RecipeMaxCount;
			t.bandCount += row.BandCount;
			t.outOfLimits += row.OutOfLimitsCount;

			totals.set(roll, t);
		}
	}

	return totals;
};

const ratio = (sum: number, count: number): number | null =>
	count ? sum / count : null;

/**
 * The aggregate grid over the ticked groups. `undefined` marks a roll the
 * selection never produced, rendered as an inactive cell.
 */
export const buildSummary = (
	groups: StraightenerGroupRow[],
	selectedKeys: string[],
	rolls: string[],
): SummaryValues => {
	const summary: SummaryValues = {
		actual: {},
		target: {},
		maxActual: {},
		minActual: {},
		maxRecipe: {},
		minRecipe: {},
		percOutOfLimits: {},
		stdWithRecipe: {},
	};

	const totals = totalsByRoll(groups, selectedKeys, rolls);

	for (const roll of rolls) {
		const t = totals.get(roll);
		if (!t) {
			for (const key of Object.keys(summary) as SummaryKey[]) {
				summary[key][roll] = undefined;
			}
			continue;
		}

		const mean = ratio(t.actualSum, t.actualCount);
		summary.actual[roll] = mean;
		summary.target[roll] = ratio(t.targetSum, t.targetCount);
		summary.maxActual[roll] = t.max;
		summary.minActual[roll] = t.min;
		summary.maxRecipe[roll] = ratio(t.recipeMaxSum, t.recipeMaxCount);
		summary.minRecipe[roll] = ratio(t.recipeMinSum, t.recipeMinCount);
		summary.percOutOfLimits[roll] = t.bandCount
			? (100 * t.outOfLimits) / t.bandCount
			: null;
		// Population deviation from the pooled sums: sqrt(E[x²] − mean²). Clamped
		// at zero, since rounding can push a flat series a hair below it.
		summary.stdWithRecipe[roll] =
			mean === null
				? null
				: Math.sqrt(Math.max(0, t.sumSquares / t.actualCount - mean * mean));
	}

	return summary;
};

/** Aggregate lines for the chart, on the same basis as the summary. */
export const buildChartSeries = (
	groups: StraightenerGroupRow[],
	selectedKeys: string[],
	rolls: string[],
): ChartSeries => {
	const summary = buildSummary(groups, selectedKeys, rolls);
	const pick = (key: SummaryKey) => rolls.map((r) => summary[key][r] ?? null);

	return {
		actual: pick("actual"),
		target: pick("target"),
		minActual: pick("minActual"),
		maxActual: pick("maxActual"),
		minRecipe: pick("minRecipe"),
		maxRecipe: pick("maxRecipe"),
	};
};

/**
 * The legend line between the two grids: one recipe name, or a note when the
 * selection mixes recipes or has none.
 */
export const buildRecipeLegend = (
	groups: StraightenerGroupRow[],
	selectedKeys: string[],
): string => {
	const NO_RECIPE = "(no recipe)";
	const names = new Set(
		groups
			.filter((g) => selectedKeys.includes(g.key))
			.flatMap((g) => g.rows)
			.map((r) => r.RecipeName ?? NO_RECIPE),
	);

	if (names.size === 0) return "No recipes on the resultset.";
	if (names.size === 1) {
		const [name] = [...names];
		if (name === NO_RECIPE)
			return "The product has no current straightener recipe.";
		if (name) return name;
	}
	return "There are multiple recipes on the resultset.";
};
